import { createApi } from '@reduxjs/toolkit/query/react';
import { communicationHubApi } from './communicationHub';
import { warehousePackagesApi } from './warehouse/packages';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import moment from 'moment';
import React from 'react';

export const purchaseOrderApi = createApi({
  tagTypes: [
    'PURCHASE_ORDER_LIST',
    'PURCHASE_ORDER_TAGS',
    'PURCHASE_ORDER_ITEM_LIST',
    'PURCHASE_ORDER',
    'PURCHASE_ORDER_ITEM',
    'PURCHASE_ORDER_SUPPLIER_LIST',
    'PURCHASE_ORDER_SUPPLIER_OFFER',
    'PURCHASE_ORDER_ITEM_BRANDS',
  ],

  reducerPath: 'purchaseOrderApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    purchaseOrderList: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };

        return {
          url: `/wrd/api/purchase-order/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        const content = response?.data?.content[0] || {};
        const { managers = {}, purchaseOrders = [] } = content;

        if (purchaseOrders.length) {
          content.purchaseRequestResponses = purchaseOrders.map((item: any) => ({
            ...item,
            postSalesManager: managers[item.postSalesManagerId],
            postSalesManagerAssistant: managers[item.postSalesManagerAssistantId],
            formattedLastModifiedDate: {
              full: moment(item.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
              day: moment.utc(item.lastModifiedDate).format('DD.MM.YYYY'),
            },
            formattedCreatedDate: {
              full: moment(item.createDate).format('DD.MM.YYYY HH:mm:ss'),
              day: moment.utc(item.createDate).format('DD.MM.YYYY'),
            },
          }));
        }
        return response.data;
      },
      providesTags: () => ['PURCHASE_ORDER_LIST'],
    }),
    purchaseOrderItemsList: builder.query<any, any>({
      query: (args) => {
        const { purchaseOrderId, size = 500, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };

        return {
          url: `/wrd/api/purchase-order/${purchaseOrderId}/items/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to match expected pagination structure
        const items = response?.data || [];
        const transformedItems = items.map((item: any, index: number) => ({
          ...item,
          key: item.id || `item-${index}`, // Add key for table
        }));

        return {
          content: transformedItems,
          totalElements: items.length, // Since API doesn't provide pagination info
          totalPages: 1,
          size: items.length,
          number: 0,
        };
      },
      providesTags: () => ['PURCHASE_ORDER_LIST'],
    }),
    purchaseOrderCreateItemsList: builder.query<any, any>({
      query: (args) => {
        const { size = 500, page = 1, sortField, sortOrder, search, filters, group } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, group, ...(typeof search !== 'undefined' && { search }) };

        return {
          url: `/wrd/api/purchase-order-item/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        return response.data;
      },
      providesTags: () => ['PURCHASE_ORDER_LIST'],
    }),
    purchaseOrderItemPurchasePriceList: builder.query<any, any>({
      query: (args) => {
        const { sku } = args;
        return {
          url: `/wrd/api/global-supplier-offer/purchase-prices/${sku}`,
        };
      },
      transformResponse: (response: any) => {
        return response.data;
      },
      providesTags: () => ['PURCHASE_ORDER_LIST'],
    }),
    purchaseOrderById: builder.query<any, any>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/purchase-order/${id}/details`,
        };
      },
      transformResponse: (response: any) => {
        const { postSalesManager, postSalesManagerAssistant } = response?.data || {};
        return {
          assignments: {
            postSalesManager: postSalesManager,
            postSalesManagerAssistant: postSalesManagerAssistant,
          },
          purchaseOrder: {
            ...response.data,
            formattedLastModifiedDate: moment(response.data.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
            formattedCreatedDate: moment(response.data.createDate).format('DD.MM.YYYY HH:mm:ss'),
          },
        };
      },
      providesTags: () => ['PURCHASE_ORDER'],
    }),
    purchaseOrderItemSalesPrice: builder.query<any, any>({
      query: (args) => {
        const { ids } = args;
        return {
          url: `/wrd/api/purchase-order-item/commercial-offers?ids=${ids}`,
        };
      },
      transformResponse: (response: any) => {
        return response.data;
      },
    }),
    purchaseOrderBrands: builder.query<any, any>({
      query: (args) => {
        const { purchaseOrderId } = args;
        return {
          url: `/wrd/api/purchase-order/brands${purchaseOrderId ? '?purchaseOrderId=' + purchaseOrderId : ''}`,
        };
      },
      transformResponse: (response: any) => {
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
    }),
    purchaseOrderCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/purchase-order',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['PURCHASE_ORDER_LIST', 'PURCHASE_ORDER', 'PURCHASE_ORDER_ITEM_BRANDS'],
    }),
    purchaseOrderStatusChange: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/purchase-order/status',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['PURCHASE_ORDER_LIST', 'PURCHASE_ORDER'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(warehousePackagesApi.util.invalidateTags(['INBOUND_PACKAGES', 'INBOUND_PACKAGE_DETAILS']));
        } catch {
          // Error is handled by the component
        }
      },
    }),
    updatePurchaseOrderManagement: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/purchase-order/management',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST'],
    }),
    updatePurchaseOrderShipping: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/purchase-order/shipping-information',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST'],
    }),
    generatePurchaseOrderPdf: builder.mutation<any, { purchaseOrderId: string }>({
      query: ({ purchaseOrderId }) => ({
        url: `/wrd/api/purchase-order/generate-purchase-order/${purchaseOrderId}`,
        method: 'post',
      }),
      invalidatesTags: () => ['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST'],
    }),
    sendPurchaseOrder: builder.mutation<any, any>({
      query: ({ data }) => ({
        url: `/wrd/api/request-price/purchase-order`,
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(communicationHubApi.util.invalidateTags(['NOTE', 'NOTE_LIST']));
      },
      invalidatesTags: () => ['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST'],
    }),
    purchaseOrderRequestPrice: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/request-price/order`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST'],
    }),
    purchaseOrderItemHistoricalData: builder.query<any, any>({
      query: (args) => {
        const { sku, supplierId, dateRange } = args;
        return {
          url: `/wrd/api/supplier-offer/historical-data/${sku}`,
          params: { supplierId, range: dateRange },
        };
      },
      transformResponse: (_response: any) => {
        return _response.data;
      },
      providesTags: () => ['PURCHASE_ORDER_LIST'],
    }),
    purchaseOrderItemBrands: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/purchase-order-item/brands`,
        };
      },
      transformResponse: (_response: any) => {
        return _response.data;
      },
      providesTags: () => ['PURCHASE_ORDER_ITEM_BRANDS'],
    }),
    purchaseOrderItemBrandsLock: builder.mutation<any, any>({
      query: (brandIds) => {
        return {
          url: `/wrd/api/purchase-order-item/lock-brands?brandIds=${brandIds}`,
          method: 'post',
        };
      },
      invalidatesTags: () => ['PURCHASE_ORDER_ITEM_BRANDS'],
    }),
    purchaseOrderItemBrandsUnlock: builder.mutation<any, void>({
      query: () => {
        return {
          url: `/wrd/api/purchase-order-item/unlock-brands`,
          method: 'post',
        };
      },
      invalidatesTags: () => ['PURCHASE_ORDER_ITEM_BRANDS'],
    }),
    purchaseOrderItemBrandUnlock: builder.mutation<any, string>({
      query: (brandId) => {
        return {
          url: `/wrd/api/purchase-order-item/unlock-brand?brandId=${brandId}`,
          method: 'post',
        };
      },
      invalidatesTags: () => ['PURCHASE_ORDER_ITEM_BRANDS'],
    }),
    purchaseOrderShippingClientAddress: builder.query<any, { purchaseOrderId: string }>({
      query: ({ purchaseOrderId }) => {
        return {
          url: `/wrd/api/purchase-order/shipping-client-possible?purchaseOrderId=${purchaseOrderId}`,
        };
      },
      transformResponse: (_response: any) => {
        return _response.data;
      },
    }),
    purchaseOrderCreatePreCheck: builder.query<any, React.Key[]>({
      query: (purchaseOrderItemIds) => {
        return {
          url: `/wrd/api/purchase-order/pre-create-check?purchaseOrderItemIds=${purchaseOrderItemIds}`,
        };
      },
      transformResponse: (response: any) => {
        return response.data;
      },
    }),
    purchaseOrderFollowUp: builder.mutation<any, React.Key[]>({
      query: (purchaseOrderItemIds) => {
        return {
          url: `/wrd/api/purchase-order/follow-up?purchaseOrderItemIds=${purchaseOrderItemIds}`,
          method: 'post',
        };
      },
      transformResponse: (response: any) => {
        return response.data;
      },
      invalidatesTags: () => ['PURCHASE_ORDER_LIST'],
    }),
  }),
});
export const {
  usePurchaseOrderListQuery,
  usePurchaseOrderByIdQuery,
  usePurchaseOrderItemsListQuery,
  usePurchaseOrderItemPurchasePriceListQuery,
  usePurchaseOrderStatusChangeMutation,
  useUpdatePurchaseOrderManagementMutation,
  useUpdatePurchaseOrderShippingMutation,
  usePurchaseOrderCreateMutation,
  usePurchaseOrderItemSalesPriceQuery,
  useGeneratePurchaseOrderPdfMutation,
  useSendPurchaseOrderMutation,
  usePurchaseOrderCreateItemsListQuery,
  usePurchaseOrderRequestPriceMutation,
  usePurchaseOrderBrandsQuery,
  usePurchaseOrderItemHistoricalDataQuery,
  usePurchaseOrderItemBrandsQuery,
  usePurchaseOrderItemBrandsLockMutation,
  usePurchaseOrderItemBrandsUnlockMutation,
  usePurchaseOrderShippingClientAddressQuery,
  usePurchaseOrderItemBrandUnlockMutation,
  useLazyPurchaseOrderCreatePreCheckQuery,
  usePurchaseOrderFollowUpMutation,
} = purchaseOrderApi;
